import { format as dateFnFormat, fromZonedTime, getTimezoneOffset, toZonedTime } from 'date-fns-tz';
import { de, enUS, es, fr, nl } from 'date-fns/locale';
import {
  differenceInCalendarDays, differenceInHours, differenceInMinutes, differenceInMonths, isValid, parse, startOfDay,
  subMinutes,
} from 'date-fns';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';

import { LocaleContext } from './LocaleProvider';
import { parseNumber } from './helpers';
import config, { DateTimeFormat } from '../config';
import useCurrencies from './useCurrencies';

const localeCodes = {
  de: 'de',
  en: 'en-US',
  es: 'es',
  nl: 'nl',
  fr: 'fr',
};

const dateFnLocales = {
  de,
  en: enUS,
  es,
  nl,
  fr,
};

interface ParseDateOptions {
  format?: DateTimeFormat;
  timezone?: string | null;
}

interface FormatDateOptions {
  format?: DateTimeFormat;
  timezone?: string;
}

interface TodayOptions {
  timezone?: string;
}

const useLocale = () => {
  const { t } = useTranslation('common');
  const { getCurrencySymbol } = useCurrencies();
  const locale = useContext(LocaleContext);

  /**
   * Creates a Date object from a formatted string.
   *
   * @param timezone The timezone of the given date. Project timezone by default.
   *                 If you pass `null`, no conversion will be made.
   */
  const parseDate = useCallback((
    date: string,
    {
      format = 'internal_date_time',
      timezone = locale.timezone,
    }: ParseDateOptions = {},
  ) => {
    const parsedDate = parse(date, locale.dateFormats[format], new Date());

    if (!timezone) {
      return parsedDate;
    }

    return fromZonedTime(parsedDate, timezone);
  }, [locale.dateFormats, locale.timezone]);

  /**
   * Converts a Date object into a formatted string.
   *
   * @param timezone The timezone in which the date should be formatted. Project timezone by
   *                 default. If you pass `null`, no conversion will be made.
   */
  const formatDate = (
    date: Date,
    {
      format = 'display_date_time',
      timezone = locale.timezone,
    }: FormatDateOptions = {},
  ) => {
    const parsedDate = timezone ? toZonedTime(date, timezone) : date;

    return dateFnFormat(parsedDate, locale.dateFormats[format], {
      locale: dateFnLocales[locale.locale],
      timeZone: timezone,
    });
  };

  const formatRelativeDate = useCallback((value: string) => {
    const date = new Date(value);
    const now = new Date();

    if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') {
      // Browser not supported
      return date.toLocaleString();
    }

    // Format date relative to now using Intl.RelativeTimeFormat
    const formatter = new Intl.RelativeTimeFormat(localeCodes[locale.locale], { numeric: 'auto' });

    const monthsDiff = differenceInMonths(date, now);

    if (monthsDiff !== 0) {
      return formatter.format(monthsDiff, 'month');
    }

    const daysDiff = differenceInCalendarDays(date, now);

    if (daysDiff !== 0) {
      return formatter.format(daysDiff, 'day');
    }

    const hoursDiff = differenceInHours(date, now);

    if (hoursDiff !== 0) {
      return formatter.format(hoursDiff, 'hour');
    }

    const minutesDiff = differenceInMinutes(date, now);

    if (minutesDiff !== 0) {
      return formatter.format(minutesDiff, 'minute');
    }

    return t('common:locale.now');
  }, [locale.locale, t]);

  /**
   * Creates a Date object initialized to today's date in the project's timezone, in UTC.
   *
   * @param timezone The timezone of the given date. Project timezone by default.
   */
  const today = ({ timezone = locale.timezone }: TodayOptions = {}) => {
    const offset = getTimezoneOffset(timezone) / 1000 / 60;
    return subMinutes(startOfDay(toZonedTime(new Date(), timezone)), offset);
  };

  const dateIsValid = (date: string, format: DateTimeFormat = 'internal_date_time') => (
    isValid(parse(date, locale.dateFormats[format], new Date()))
  );

  const formatNumber = (
    value: number,
    options: Intl.NumberFormatOptions = { maximumFractionDigits: 2, useGrouping: true },
  ) => (
    value.toLocaleString(localeCodes[locale.locale], options)
  );

  const formatInteger = (
    value: number,
    options: Intl.NumberFormatOptions = { maximumFractionDigits: 0, useGrouping: true },
  ) => (
    value.toLocaleString(localeCodes[locale.locale], options)
  );

  const parseInteger = (value: string) => (
    Math.floor(parseNumber(value))
  );

  const formatCurrency = (
    amount: number,
    options: Intl.NumberFormatOptions = {},
  ) => {
    const symbol = getCurrencySymbol(locale.currency);
    const sign = amount < 0 || Object.is(amount, -0) ? '-' : '';
    const value = Math.abs(amount / 100).toLocaleString(localeCodes[locale.locale], {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options,
    });

    if (locale.locale === 'de' || locale.locale === 'fr' || locale.locale === 'es') {
      // In German, Spanish and French it is customary to place the currency symbol behind the amount
      return `${sign}${value} ${symbol}`.trim();
    }

    return `${sign ? `${sign} ` : ''}${symbol} ${value}`.trim();
  };

  const parseCurrency = (value: any) => {
    // Remove everything but digits, commas and dots.
    const stripped = value.replace(/[^\d,.]/g, '');

    // Check if it ends with one or two decimals.
    const oneDecimal = stripped.match(/(,|\.)\d{1}$/);
    const twoDecimals = stripped.match(/(,|\.)\d{2}$/);

    // Remove all non-digits
    const digits = parseInteger(stripped.replace(/[^\d]/g, ''));

    const result = twoDecimals ? digits : (oneDecimal ? digits * 10 : digits * 100);

    const sign = value.startsWith('-') ? -1 : 1;

    return sign * result;
  };

  // List of locales, sorted by language name.
  const sortedLocales = sortBy(config.locales, (locale) => t(`locales.${locale}`));

  const getDateFilterMappers = () => {
    // 2026-04-22 (local) => 2026-04-22 00:00:00 (local) => 2026-04-21 22:00:00 (UTC)
    const dateAfterToServer = (after: string) => formatDate(
      parseDate(after, { format: 'internal_date' }),
      { format: 'internal_date_time', timezone: 'UTC' },
    );

    // 2026-04-23 (local) => 2026-04-24 00:00:00 (local) => 2026-04-23 22:00:00 (UTC)
    const dateBeforeToServer = (before: string) => {
      const date = parseDate(before, { format: 'internal_date' });
      date.setDate(date.getDate() + 1);
      return formatDate(date, { format: 'internal_date_time', timezone: 'UTC' });
    };

    // 2026-04-21 22:00:00 (UTC) => 2026-04-22 00:00:00 (local) => 2026-04-22 (local)
    const dateAfterToFilter = (after: string) => formatDate(
      parseDate(after, { format: 'internal_date_time', timezone: 'UTC' }),
      { format: 'internal_date' },
    );

    // 2026-04-23 22:00:00 (UTC) => 2026-04-24 00:00:00 (local) => 2026-04-23 (local)
    const dateBeforeToFilter = (before: string) => {
      const date = parseDate(before, { format: 'internal_date_time', timezone: 'UTC' });
      date.setDate(date.getDate() - 1);
      return formatDate(date, { format: 'internal_date' });
    };

    return { dateAfterToServer, dateBeforeToServer, dateAfterToFilter, dateBeforeToFilter };
  };

  return {
    ...locale,
    sortedLocales,
    parseDate,
    formatDate,
    formatRelativeDate,
    today,
    dateIsValid,
    formatNumber,
    parseNumber,
    formatInteger,
    parseInteger,
    formatCurrency,
    parseCurrency,
    getDateFilterMappers,
  };
};

export default useLocale;
